// apps/client/src/hooks/useClawbacks.ts
import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  ClawbackReason,
  ClawbackMetricsByReason,
} from '../types/clawbackReasons';

interface UseClawbacksParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  brokerId?: string;
  officeId?: string;
  templateId?: string;
  entityCode?: string;
}

interface ClawbackRecord {
  id: string;
  broker: string;
  brokerId: string;
  customerName: string | null;
  loanId: string;
  invoicedDate: Date;
  /**
   * 'YYYY-MM' of the invoice month, computed SERVER-side with the same expression
   * the trail-risk materializer uses, so it keys the follow-up ref exactly. Never
   * derive this from `invoicedDate` on the client: the browser timezone can shift a
   * 1st-of-month date into the previous month and the ref would miss its event.
   */
  periodMonth: string | null;
  settlementDate: Date;
  loanAmount: number;
  amountExGst: number;
  comments: string;
  clawbackPercentage: number;
  isClawback: boolean;
  lender: string | null;
  paymentType: string;
  // Clawback reason fields
  clawbackReason: ClawbackReason | null;
  reasonSetByEmail: string | null;
  reasonSetAt: string | null;
  reasonNotes: string | null;
}

interface BrokerBreakdown {
  brokerId: string;
  brokerDisplay: string;
  clawbackCount: number;
  clawbackAmountCents: number;
}

interface TemplateBreakdown {
  templateId: string;
  clawbackCount: number;
}

interface SetClawbackReasonOptions {
  clawbackIds: string[];
  loanIds?: string[];
  reason: ClawbackReason;
  notes?: string;
}

interface UseClawbacksReturn {
  clawbacks: ClawbackRecord[];
  loading: boolean;
  error: string | null;
  totalClawbackCount: number;
  totalClawbackAmountCents: number;
  totalUpfrontCommissionCents: number;
  totalTrailCommissionCents: number;
  clawbackRate: number;
  brokerBreakdown: BrokerBreakdown[];
  templateBreakdown: TemplateBreakdown[];
  metricsByReason: ClawbackMetricsByReason | null;
  // Reason setting
  setClawbackReason: (options: SetClawbackReasonOptions) => Promise<boolean>;
  isSaving: boolean;
  // Selection state for bulk operations
  selectedIds: Set<string>;
  toggleSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  // Refresh
  refresh: () => void;
}

/**
 * Hook to fetch clawback report from BigQuery
 *
 * Queries monthly_settlement_aggregates table to analyze commission clawbacks.
 * Returns individual clawback records reconstructed from aggregate data.
 *
 * RBAC: Automatically filters to broker's data if brokerId is provided
 */
export function useClawbacks({
  companyId,
  year,
  yearType,
  brokerId,
  officeId,
  templateId,
  entityCode,
}: UseClawbacksParams): UseClawbacksReturn {
  const [clawbacks, setClawbacks] = useState<ClawbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalClawbackCount, setTotalClawbackCount] = useState(0);
  const [totalClawbackAmountCents, setTotalClawbackAmountCents] = useState(0);
  const [totalUpfrontCommissionCents, setTotalUpfrontCommissionCents] = useState(0);
  const [totalTrailCommissionCents, setTotalTrailCommissionCents] = useState(0);
  const [clawbackRate, setClawbackRate] = useState(0);
  const [brokerBreakdown, setBrokerBreakdown] = useState<BrokerBreakdown[]>([]);
  const [templateBreakdown, setTemplateBreakdown] = useState<TemplateBreakdown[]>([]);
  const [metricsByReason, setMetricsByReason] = useState<ClawbackMetricsByReason | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchClawbacks = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`[useClawbacks] Calling BigQuery getClawbackReport:`, {
          companyId,
          year,
          yearType,
          brokerId: brokerId || 'all',
          officeId: officeId || 'all',
          templateId: templateId || 'all',
        });

        // Call BigQuery function
        const getClawbackReportFunction = httpsCallable(functions, 'getClawbackReport');
        const result = await getClawbackReportFunction({
          companyId,
          year,
          yearType,
          brokerId,
          officeId,
          templateId,
          entityCode,
        });

        const data = result.data as {
          totalClawbackCount: number;
          totalClawbackAmountCents: number;
          clawbackRate: number;
          records: Array<{
            id: string;
            brokerId: string;
            brokerDisplay: string;
            customerName: string | null;
            loanId: string;
            settlementDate: string;
            invoicedDate: string | null;
            periodMonth: string | null;
            loanAmountCents: number;
            commissionCents: number;
            clawbackPercentage: number;
            lender: string | null;
            paymentType: string;
            comments: string | null;
            // Clawback reason fields
            clawbackReason: ClawbackReason | null;
            reasonSetByEmail: string | null;
            reasonSetAt: string | null;
            reasonNotes: string | null;
          }>;
          monthlyTrend: Array<{
            month: string;
            clawbackCount: number;
            clawbackAmountCents: number;
            grossCommissionCents: number;
            clawbackRatePercent: number;
          }>;
          brokerBreakdown: Array<{
            brokerId: string;
            brokerDisplay: string;
            clawbackCount: number;
            clawbackAmountCents: number;
          }>;
          templateBreakdown: Array<{
            templateId: string;
            clawbackCount: number;
          }>;
          summary: {
            totalGrossCommissionCents: number;
            totalUpfrontCommissionCents: number;
            totalTrailCommissionCents: number;
            totalNetCommissionCents: number;
            affectedRecordCount: number;
          };
          metricsByReason: ClawbackMetricsByReason;
        };

        console.log(`[useClawbacks] BigQuery returned:`, {
          totalClawbackCount: data.totalClawbackCount,
          totalClawbackAmountCents: data.totalClawbackAmountCents / 100,
          clawbackRate: data.clawbackRate.toFixed(2) + '%',
          records: data.records.length,
          totalUpfrontCommissionCents: data.summary?.totalUpfrontCommissionCents,
          brokerBreakdownCount: data.brokerBreakdown?.length || 0,
          brokerBreakdown: data.brokerBreakdown?.slice(0, 3), // Log first 3 for debugging
        });

        // Store aggregate metrics
        setTotalClawbackCount(data.totalClawbackCount);
        setTotalClawbackAmountCents(data.totalClawbackAmountCents);
        setTotalUpfrontCommissionCents(data.summary?.totalUpfrontCommissionCents || 0);
        setTotalTrailCommissionCents(data.summary?.totalTrailCommissionCents || 0);
        setClawbackRate(data.clawbackRate);
        setBrokerBreakdown(data.brokerBreakdown || []);
        setTemplateBreakdown(data.templateBreakdown || []);
        setMetricsByReason(data.metricsByReason || null);
        setSelectedIds(new Set()); // Clear selection on new fetch

        // Transform BigQuery records to frontend format
        // NOTE: Keep values in CENTS - formatCurrency() expects cents and divides by 100
        const transformedRecords: ClawbackRecord[] = data.records.map((record) => ({
          id: record.id,
          broker: record.brokerDisplay,
          brokerId: record.brokerId,
          customerName: record.customerName,
          loanId: record.loanId,
          settlementDate: new Date(record.settlementDate),
          invoicedDate: record.invoicedDate ? new Date(record.invoicedDate) : new Date(record.settlementDate),
          periodMonth: record.periodMonth ?? null,
          loanAmount: record.loanAmountCents, // Keep in cents - formatCurrency divides by 100
          amountExGst: Math.abs(record.commissionCents), // Keep in cents - formatCurrency divides by 100
          comments: record.comments || `${record.paymentType} clawback${record.lender ? ` - ${record.lender}` : ''}`,
          clawbackPercentage: record.clawbackPercentage, // Now extracted from comments field
          isClawback: true,
          lender: record.lender,
          paymentType: record.paymentType,
          // Clawback reason fields
          clawbackReason: record.clawbackReason,
          reasonSetByEmail: record.reasonSetByEmail,
          reasonSetAt: record.reasonSetAt,
          reasonNotes: record.reasonNotes,
        }));

        setClawbacks(transformedRecords);
      } catch (err: any) {
        console.error('[useClawbacks] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch clawback data');
        setClawbacks([]);
        setTotalClawbackCount(0);
        setTotalClawbackAmountCents(0);
        setTotalUpfrontCommissionCents(0);
        setTotalTrailCommissionCents(0);
        setClawbackRate(0);
        setBrokerBreakdown([]);
        setTemplateBreakdown([]);
        setMetricsByReason(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClawbacks();
  }, [companyId, year, yearType, brokerId, officeId, templateId, entityCode, refreshTrigger]);

  // Set clawback reason(s)
  const setClawbackReason = useCallback(async (options: SetClawbackReasonOptions): Promise<boolean> => {
    if (!companyId || options.clawbackIds.length === 0) {
      return false;
    }

    // Optimistic update
    const previousClawbacks = [...clawbacks];
    const previousMetrics = metricsByReason ? { ...metricsByReason } : null;

    // Update clawbacks locally
    setClawbacks(prev =>
      prev.map(clawback =>
        options.clawbackIds.includes(clawback.id)
          ? {
              ...clawback,
              clawbackReason: options.reason,
              reasonNotes: options.notes || null,
              reasonSetAt: new Date().toISOString(),
            }
          : clawback
      )
    );

    // Update metrics optimistically
    if (metricsByReason) {
      const affectedClawbacks = previousClawbacks.filter(c => options.clawbackIds.includes(c.id));
      const affectedAmountCents = affectedClawbacks.reduce((sum, c) => sum + c.amountExGst, 0);

      setMetricsByReason(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          byReason: { ...prev.byReason },
          byReasonAmountCents: { ...prev.byReasonAmountCents },
        };

        // Decrease old reason counts
        for (const clawback of affectedClawbacks) {
          const oldReason = clawback.clawbackReason;
          if (oldReason) {
            updated.byReason[oldReason] = Math.max(0, (updated.byReason[oldReason] || 0) - 1);
            updated.byReasonAmountCents[oldReason] = Math.max(0, (updated.byReasonAmountCents[oldReason] || 0) - clawback.amountExGst);
          } else {
            updated.needsCategorization = Math.max(0, updated.needsCategorization - 1);
            updated.needsCategorizationAmountCents = Math.max(0, updated.needsCategorizationAmountCents - clawback.amountExGst);
          }
        }

        // Increase new reason counts
        updated.byReason[options.reason] = (updated.byReason[options.reason] || 0) + affectedClawbacks.length;
        updated.byReasonAmountCents[options.reason] = (updated.byReasonAmountCents[options.reason] || 0) + affectedAmountCents;

        return updated;
      });
    }

    // Clear selection
    setSelectedIds(new Set());

    // Make API call
    setIsSaving(true);
    try {
      console.log('[useClawbacks] Calling setClawbackReason:', {
        companyId,
        templateId,
        clawbackIds: options.clawbackIds,
        loanIds: options.loanIds,
        reason: options.reason,
        notes: options.notes,
      });

      const setClawbackReasonFn = httpsCallable(functions, 'setClawbackReason');
      const result = await setClawbackReasonFn({
        companyId,
        templateId,
        clawbackIds: options.clawbackIds,
        loanIds: options.loanIds,
        reason: options.reason,
        notes: options.notes,
      });

      const data = result.data as { success: boolean; updatedCount: number };
      console.log('[useClawbacks] setClawbackReason response:', data);
      if (data.success) {
        return true;
      } else {
        throw new Error('Failed to set clawback reason');
      }
    } catch (err: any) {
      // Revert optimistic update
      setClawbacks(previousClawbacks);
      setMetricsByReason(previousMetrics);
      console.error('[useClawbacks] Error setting reason:', {
        message: err?.message,
        code: err?.code,
        details: err?.details,
        err,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [companyId, templateId, clawbacks, metricsByReason]);

  // Selection handlers
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(clawbacks.map(c => c.id)));
  }, [clawbacks]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Refresh function
  const refresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return {
    clawbacks,
    loading,
    error,
    totalClawbackCount,
    totalClawbackAmountCents,
    totalUpfrontCommissionCents,
    totalTrailCommissionCents,
    clawbackRate,
    brokerBreakdown,
    templateBreakdown,
    metricsByReason,
    setClawbackReason,
    isSaving,
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    refresh,
  };
}
